// src/utils/cotizacionFormUtils.tsx
import type { CotizacionData, Departamento, Ciudad } from '../types/cotizacionFormTypes'

/**
 * Utilidad para leer meta_data
 */
export function getMeta(meta_data: any[], key: string, defaultValue: string = ''): string {
  if (!Array.isArray(meta_data)) return defaultValue
  const found = meta_data.find(m => m.key === key)
  return found ? (found.value || '') : defaultValue
}

/**
 * Verifica si una ciudad es metropolitana basándose en su código
 */
export function esCiudadMetropolitana(ciudad: string): boolean {
  if (!ciudad) return false
  // Códigos de ciudades metropolitanas
  const codigosMetropolitanos = ['05001', '05088', '05266', '05360', '05631']
  return codigosMetropolitanos.includes(ciudad)
}

/**
 * Renderiza las opciones de departamentos para un select
 */
export function renderDepartamentoOptions(
  addressType: 'billing' | 'shipping',
  formData: CotizacionData,
  deptos: Departamento[]
): JSX.Element[] {
  const options: JSX.Element[] = []
  const selectedState = addressType === 'billing' 
    ? formData.billingAddress?.state 
    : formData.shippingAddress?.state
  if (!selectedState) {
    options.push(
      <option key="default" value="">Seleccione</option>
    )
  }
  deptos.forEach((depto) => {
    options.push(
      <option key={depto.Codigo} value={depto.Codigo}>
        {depto.Nombre}
      </option>
    )
  })
  return options
}

/**
 * Renderiza las opciones de ciudades para un select, filtradas por departamento
 */
export function renderCiudadOptions(
  addressType: 'billing' | 'shipping',
  formData: CotizacionData,
  cities: Ciudad[]
): JSX.Element[] {
  const options: JSX.Element[] = []
  const selectedCity = addressType === 'billing' 
    ? formData.billingAddress?.city 
    : formData.shippingAddress?.city
  const selectedState = addressType === 'billing' 
    ? formData.billingAddress?.state 
    : formData.shippingAddress?.state
  let codigoDepartamento = ''
  if (selectedState) {
    codigoDepartamento = selectedState
  }
  if (!selectedCity) {
    options.push(
      <option key="default" value="">Seleccione</option>
    )
  }
  cities.forEach((ciudad) => {
    const perteneceAlDepartamento = codigoDepartamento 
      ? ciudad.codigo.startsWith(codigoDepartamento)
      : true
    if (perteneceAlDepartamento) {
      options.push(
        <option key={ciudad.codigo} value={ciudad.codigo}>
          {ciudad.ciudad}
        </option>
      )
    }
  })
  return options
}

/**
 * Sincroniza datos del cliente entre billingAddress, shippingAddress y customer
 */
export function synchronizeCustomerData(
  newData: any,
  section: keyof CotizacionData,
  field: string,
  value: any
): void {
  if (section === 'billingAddress') {
    newData.customer = {
      first_name: field === 'first_name' ? value : (newData.customer?.first_name || ''),
      last_name: field === 'last_name' ? value : (newData.customer?.last_name || ''),
      email: field === 'email' ? value : (newData.customer?.email || ''),
      documento: field === 'documento' ? value : (newData.customer?.documento || ''),
      tipo_documento: field === 'tipo_documento' ? value : (newData.customer?.tipo_documento || ''),
    }
    
    newData.billingAddress = {
      address_1: field === 'address_1' ? value : (newData.billingAddress?.address_1 || ''),
      city: field === 'city' ? value : (newData.billingAddress?.city || ''),
      state: field === 'state' ? value : (newData.billingAddress?.state || ''),
      phone: field === 'phone' ? value : (newData.billingAddress?.phone || ''),
      first_name: field === 'first_name' ? value : (newData.billingAddress?.first_name || ''),
      last_name: field === 'last_name' ? value : (newData.billingAddress?.last_name || ''),
    }
  }
  
  if (section === 'shippingAddress') {
    newData.shippingAddress = {
      address_1: field === 'address_1' ? value : (newData.shippingAddress?.address_1 || ''),
      city: field === 'city' ? value : (newData.shippingAddress?.city || ''),
      state: field === 'state' ? value : (newData.shippingAddress?.state || ''),
      phone: field === 'phone' ? value : (newData.shippingAddress?.phone || ''),
    }
  }
  
  // Sincronizar teléfono entre billing y shipping
  if (section === 'billingAddress' && field === 'phone') {
    newData.shippingAddress = {
      ...newData.shippingAddress,
      phone: value
    }
  }
  
  if (section === 'shippingAddress' && field === 'phone') {
    newData.billingAddress = {
      ...newData.billingAddress,
      phone: value
    }
  }
}

